import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { HeadingMedium, BodyText } from "../central/Typography";
import { useSeason } from "../context/SeasonContext";

export default function PlayerPointsModal({
  open,
  onClose,
  player,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
  currentGameweek,
  gwLabel,
  chipName = "None",
  playerGWPoints,
  breakdownRows = [],
  isCaptain = false,
  isSelected = false,
  gwRank = null,
  positionRank = null,
  formRank = null,
  seasonRank = null,
  seasonPositionRank = null,
  basePoints = null,
  finalPoints = null,
  leagueID,
  initialSeasonID,
}) {
  const resolvedGWLabel = gwLabel || currentGameweek || "GW";
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  // Read-only from context — never write back to it from inside a modal
  const { availableSeasons, selectedSeasonID, currentSeasonID } = useSeason();
  // Local season state: initialised from context, fully isolated, discarded on close
  const [modalSeasonID, setModalSeasonID] = useState(selectedSeasonID);
  // Historical player data fetched when viewing a non-current season
  const [modalPlayerData, setModalPlayerData] = useState(null);

  // Reset local state every time the modal is freshly opened
  useEffect(() => {
    if (open) {
      setModalSeasonID(initialSeasonID || selectedSeasonID);
      setSeasonDropdownOpen(false);
      setModalPlayerData(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch historical player data when modalSeasonID differs from currentSeasonID
  useEffect(() => {
    if (!open || !player?.id || !leagueID) return;
    if (modalSeasonID === currentSeasonID) {
      setModalPlayerData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "leagues", leagueID, "seasons", modalSeasonID, "players", player.id));
        if (!cancelled) setModalPlayerData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch {
        if (!cancelled) setModalPlayerData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [modalSeasonID, player?.id, leagueID, open, currentSeasonID]); // eslint-disable-line react-hooks/exhaustive-deps

  // All data display uses displayPlayer; falls back to the prop when on current season
  const displayPlayer = modalPlayerData || player;

  const totalPoints = Number(
    playerGWPoints?.totalPoints ??
      playerGWPoints?.points ??
      playerGWPoints?.total ??
      0
  );

  const resolvedBasePoints = Number(
    basePoints ??
      playerGWPoints?.basePoints ??
      totalPoints
  );

  const resolvedFinalPoints = Number(
    finalPoints ??
      playerGWPoints?.finalPoints ??
      totalPoints
  );

  const resolvedDisplayFinalPoints =
    isCaptain && resolvedFinalPoints === resolvedBasePoints
      ? resolvedBasePoints * 2
      : resolvedFinalPoints;

  const statSummary = useMemo(() => {
    const source = playerGWPoints || {};
    return [
      {
        label: "Goals",
        value: Number(source.goals || 0),
      },
      {
        label: "Assists",
        value: Number(source.assists || 0),
      },
      {
        label: "Clean Sheets",
        value: Number(source.cleanSheets || 0),
      },
      {
        label: "Bonus",
        value: Number(source.bonus || 0),
      },
      {
        label: "Yellow Cards",
        value: Number(source.yellowCards || 0),
      },
      {
        label: "Saves",
        value: Number(source.saves || 0),
      },
    ].filter((item) => item.value > 0);
  }, [playerGWPoints]);

  const resolvedBreakdownRows = useMemo(() => {
    if (breakdownRows.length) {
      const baseOrder = [
        "Appearance",
        "Starter",
        "Goals",
        "Assists",
        "Clean Sheet",
        "Yellow Cards",
        "Save Bonus",
      ];

      const presentLabels = new Set((breakdownRows || []).map((r) => r?.label).filter(Boolean));
      const desiredOrder = baseOrder.filter((label) => presentLabels.has(label));
      const orderIndex = new Map(desiredOrder.map((label, index) => [label, index]));

      return [...breakdownRows].sort((a, b) => {
        const aIndex = orderIndex.has(a.label) ? orderIndex.get(a.label) : 999;
        const bIndex = orderIndex.has(b.label) ? orderIndex.get(b.label) : 999;
        return aIndex - bIndex;
      });
    }

    const source = playerGWPoints || {};
    const fallback = [];

    if (Number(source.appearance || source.appearances || 0) !== 0) {
      fallback.push({
        label: "Appearance",
        detail: `${Number(source.appearance || source.appearances || 0)}`,
      });
    }

    if (Number(source.starter || source.starts || 0) !== 0) {
      fallback.push({
        label: "Starter",
        detail: `${Number(source.starter || source.starts || 0)}`,
      });
    }

    if (Number(source.goals || 0) !== 0) {
      fallback.push({
        label: "Goals",
        detail: `${Number(source.goals || 0)}`,
      });
    }

    if (Number(source.assists || 0) !== 0) {
      fallback.push({
        label: "Assists",
        detail: `${Number(source.assists || 0)}`,
      });
    }

    if (Number(source.cleanSheets || 0) !== 0) {
      fallback.push({
        label: "Clean Sheet",
        detail: `${Number(source.cleanSheets || 0)}`,
      });
    }

    if (Number(source.yellowCards || source.infractions || 0) !== 0) {
      fallback.push({
        label: "Yellow Cards",
        detail: `${Number(source.yellowCards || source.infractions || 0)}`,
      });
    }

    return fallback;
  }, [breakdownRows, playerGWPoints]);

  const chartRows = useMemo(() => {
    return Object.keys(displayPlayer?.pointsHistory || {})
      .sort((a, b) => {
        const aNum = Number(String(a).replace("GW", "")) || 0;
        const bNum = Number(String(b).replace("GW", "")) || 0;
        return aNum - bNum;
      })
      .map((gw) => ({ gw, value: Number(displayPlayer?.pointsHistory?.[gw] || 0) }));
  }, [displayPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  const avgPointsPerAppPlayer = (() => {
    const totalSeasonPoints = Number(displayPlayer?.stats?.totalPoints || 0);
    const totalApps = Number(displayPlayer?.stats?.appearances || 0);
    if (!totalApps) return 0;
    return totalSeasonPoints / totalApps;
  })();

  const sheetRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchCurrentYRef = useRef(null);
  const dragEnabledRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  const resetDragState = () => {
    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
    dragEnabledRef.current = false;
    setIsDraggingSheet(false);
    setDragOffsetY(0);
  };

  const handleTouchStart = (e) => {
    const sheetEl = sheetRef.current;
    const startY = e.touches?.[0]?.clientY ?? null;
    touchStartYRef.current = startY;
    touchCurrentYRef.current = startY;
    dragEnabledRef.current = !sheetEl || sheetEl.scrollTop <= 0;
  };

  const handleTouchMove = (e) => {
    if (!dragEnabledRef.current) return;

    const currentY = e.touches?.[0]?.clientY ?? null;
    touchCurrentYRef.current = currentY;

    const startY = touchStartYRef.current;
    if (typeof startY !== "number" || typeof currentY !== "number") return;

    const deltaY = currentY - startY;
    if (deltaY <= 0) {
      setDragOffsetY(0);
      return;
    }

    if (typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setIsDraggingSheet(true);
    setDragOffsetY(deltaY);
  };

  const handleTouchEnd = () => {
    const startY = touchStartYRef.current;
    const endY = touchCurrentYRef.current;
    const deltaY =
      typeof startY === "number" && typeof endY === "number"
        ? endY - startY
        : 0;

    const shouldClose = dragEnabledRef.current && deltaY > 110;

    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
    dragEnabledRef.current = false;
    setIsDraggingSheet(false);

    if (shouldClose) {
      setDragOffsetY(0);
      onClose?.();
      return;
    }

    setDragOffsetY(0);
  };

  if (!open || !player) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overscrollBehavior: "contain",
      }}
    >
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: "100%",
          maxWidth: "560px",
          maxHeight: "84vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          background: "#fff",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "1rem 1rem 1.15rem",
          boxSizing: "border-box",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.2)",
          transform: `translateY(${dragOffsetY}px)`,
          transition: isDraggingSheet ? "none" : "transform 180ms ease-out",
          WebkitOverflowScrolling: "touch",
          willChange: "transform",
        }}
      >
        <div
          style={{
            width: "44px",
            height: "5px",
            borderRadius: "999px",
            background: "#d1d5db",
            margin: "0 auto 0.85rem",
          }}
        />

        {/* Top row: season pill + Done */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.6rem", marginBottom: "0.6rem" }}>
          {availableSeasons && availableSeasons.length > 1 && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setSeasonDropdownOpen((o) => !o)}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "999px",
                  background: "#f4f7f8",
                  color: "#444",
                  fontSize: "0.78rem",
                  fontWeight: "600",
                  padding: "0.25rem 0.6rem",
                  cursor: "pointer",
                  lineHeight: 1.3,
                }}
              >
                {modalSeasonID} ▾
              </button>
              {seasonDropdownOpen && (
                <div style={{ position: "absolute", right: 0, top: "110%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 10, minWidth: "90px", overflow: "hidden" }}>
                  {availableSeasons.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => { setModalSeasonID(s); setSeasonDropdownOpen(false); }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: s === modalSeasonID ? "#f0fafb" : "#fff",
                        border: "none",
                        borderBottom: "1px solid #f0f0f0",
                        padding: "0.5rem 0.85rem",
                        fontSize: "0.82rem",
                        fontWeight: s === modalSeasonID ? "700" : "500",
                        color: s === modalSeasonID ? "#50B4C8" : "#333",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: "0.95rem", fontWeight: "600", color: "#555", cursor: "pointer", padding: 0, lineHeight: 1 }}
          >
            Done
          </button>
        </div>

        {/* Centered name block with top prev/next arrows */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.75rem" }}>
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Previous player"
            style={{ background: "none", border: "none", fontSize: "1.2rem", fontWeight: "700", color: canPrev ? "#50B4C8" : "#ddd", cursor: canPrev ? "pointer" : "default", padding: "0.2rem 0.3rem", lineHeight: 1, flexShrink: 0 }}
          >‹</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <HeadingMedium style={{ margin: 0, fontSize: "1.2rem" }}>
              {player.name}
              {playerGWPoints?.isGK === true && <span style={{ fontSize: "0.95rem", marginLeft: "0.35rem" }} aria-label="Goalkeeper">🧤</span>}
            </HeadingMedium>
            <BodyText style={{ margin: "0.2rem 0 0", fontSize: "0.86rem", color: "#666" }}>
              {player.position} • {resolvedGWLabel}
            </BodyText>
            {modalSeasonID !== currentSeasonID && availableSeasons && availableSeasons.length > 1 && (
              <BodyText style={{ margin: "0.15rem 0 0", fontSize: "0.78rem", color: "#999" }}>
                Viewing {modalSeasonID} data
              </BodyText>
            )}
            {(isCaptain || isSelected) && (
              <div style={{ display: "flex", justifyContent: "center", gap: "0.4rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
                {isCaptain && (
                  <span style={{ borderRadius: "999px", background: "#ffd54f", padding: "0.18rem 0.55rem", fontSize: "0.72rem", fontWeight: "700", color: "#5c4300", lineHeight: 1.2 }}>
                    Captain
                  </span>
                )}
                {isSelected && (
                  <span style={{ borderRadius: "999px", background: "#eef2f4", padding: "0.18rem 0.55rem", fontSize: "0.72rem", fontWeight: "700", color: "#234", lineHeight: 1.2 }}>
                    Selected
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Next player"
            style={{ background: "none", border: "none", fontSize: "1.2rem", fontWeight: "700", color: canNext ? "#50B4C8" : "#ddd", cursor: canNext ? "pointer" : "default", padding: "0.2rem 0.3rem", lineHeight: 1, flexShrink: 0 }}
          >›</button>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.9rem",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: "100px",
              background: "#f4f7f8",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              padding: "0.65rem 0.8rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              {resolvedGWLabel} Points
            </BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color:
                  totalPoints > 5
                    ? "#16a34a"
                    : totalPoints < 2
                    ? "#dc2626"
                    : "#111",
              }}
            >
              {totalPoints}
            </BodyText>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: "100px",
              background: "#f4f7f8",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              padding: "0.65rem 0.8rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              {resolvedGWLabel} Rank
            </BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color: gwRank && gwRank <= 3 ? "#16a34a" : "#111",
              }}
            >
              {gwRank ? `#${gwRank}` : "—"}
            </BodyText>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: "100px",
              background: "#f4f7f8",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              padding: "0.65rem 0.8rem",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
              {resolvedGWLabel} Pos. Rank
            </BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color: positionRank && positionRank <= 1 ? "#16a34a" : "#111",
              }}
            >
              {positionRank ? `#${positionRank}` : "—"}
            </BodyText>
          </div>
        </div>


        {!!statSummary.length && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "16px",
              padding: "0.8rem 0.9rem",
              marginBottom: "0.9rem",
              background: "#fff",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.82rem", fontWeight: "700", color: "#111" }}>
              Matchweek Summary
            </BodyText>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.45rem",
                marginTop: "0.65rem",
              }}
            >
              {statSummary.map((item) => (
                <div
                  key={item.label}
                  style={{
                    borderRadius: "999px",
                    background: "#eef2f4",
                    padding: "0.42rem 0.72rem",
                    fontSize: "0.82rem",
                    fontWeight: "700",
                    color: "#234",
                  }}
                >
                  {item.label} {item.value}
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            overflow: "hidden",
            marginBottom: "0.9rem",
          }}
        >
          <div
            style={{
              padding: "0.8rem 0.9rem",
              borderBottom: "1px solid #eef2f4",
              background: "#fafbfc",
            }}
          >
            <BodyText style={{ margin: 0, fontSize: "0.82rem", fontWeight: "700", color: "#111" }}>
              Points Breakdown
            </BodyText>
          </div>

          {(() => {
            const chipExtraPoints = Math.max(resolvedDisplayFinalPoints - resolvedBasePoints, 0);
            const hasNamedChip = Boolean(chipName && chipName !== "None");
            const isTripleCaptain = chipName === "Triple Captain" && isCaptain;

            const chipRow = (() => {
              if (!hasNamedChip) return null;
              if (isTripleCaptain) {
                return { label: "Triple Captain", value: "×3" };
              }
              if ((chipName === "Goal Boost" || chipName === "Fortress") && chipExtraPoints > 0) {
                return { label: chipName, value: `${chipExtraPoints}` };
              }
              return null;
            })();

            const filteredRows = resolvedBreakdownRows.filter(
              (r) =>
                ![
                  "Captain Multiplier",
                  "Triple Captain",
                  "Goal Boost",
                  "Fortress",
                  "Final Points",
                ].includes(r.label)
            );

            const rows = [
              ...filteredRows,
              { label: "Base Points", value: `${resolvedBasePoints}` },
              ...(chipRow ? [chipRow] : []),
              ...(!chipRow && isCaptain && resolvedDisplayFinalPoints !== resolvedBasePoints
                ? [
                    {
                      label: "Captain Multiplier",
                      value: `×${Math.round(resolvedDisplayFinalPoints / Math.max(resolvedBasePoints, 1))}`,
                    },
                  ]
                : []),
              ...(resolvedDisplayFinalPoints !== resolvedBasePoints
                ? [{ label: "Final Points", value: `${resolvedDisplayFinalPoints}` }]
                : []),
            ];

            return rows.length ? (
              rows.map((row, index) => (
                <div
                  key={`${row.label}-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.78rem 0.9rem",
                    background:
                      row.label === "Base Points" || row.label === "Final Points"
                        ? "#fafbfc"
                        : "#fff",
                    borderTop: index === 0 ? "none" : "1px solid #eef2f4",
                    gap: "0.75rem",
                  }}
                >
                  <BodyText
                    style={{
                      margin: 0,
                      fontSize: "0.88rem",
                      color: "#234",
                      fontWeight:
                        row.label === "Base Points" || row.label === "Final Points"
                          ? "700"
                          : "500",
                    }}
                  >
                    {row.label}
                  </BodyText>
                  <BodyText
                    style={{
                      margin: 0,
                      fontSize: "0.88rem",
                      color: "#111",
                      fontWeight:
                        row.label === "Base Points" || row.label === "Final Points"
                          ? "700"
                          : "500",
                      textAlign: "right",
                    }}
                  >
                    {(() => {
                      const raw = row.value ?? row.detail ?? "";
                      if (typeof raw !== "string") return raw;
                      if (raw.startsWith("+")) {
                        return raw.slice(1);
                      }
                      return raw;
                    })()}
                  </BodyText>
                </div>
              ))
            ) : (
              <div style={{ padding: "0.9rem" }}>
                <BodyText style={{ margin: 0, fontSize: "0.82rem", color: "#666" }}>
                  No detailed breakdown available yet.
                </BodyText>
              </div>
            );
          })()}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.9rem",
          }}
        >
          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Season Points</BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1.05rem", fontWeight: "700" }}>
              {displayPlayer?.stats?.totalPoints ?? "—"}
            </BodyText>
          </div>

          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Season Apps</BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1.05rem", fontWeight: "700" }}>
              {displayPlayer?.stats?.appearances ?? "—"}
            </BodyText>
          </div>

          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Points/App</BodyText>
            <BodyText style={{ margin: "0.15rem 0 0", fontSize: "1.05rem", fontWeight: "700" }}>
              {avgPointsPerAppPlayer.toFixed(1)}
            </BodyText>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.9rem",
          }}
        >
          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Form Rank</BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color: formRank && formRank <= 3 ? "#16a34a" : "#111",
              }}
            >
              {formRank ? `#${formRank}` : "—"}
            </BodyText>
          </div>

          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Season Rank</BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color: seasonRank && seasonRank <= 3 ? "#16a34a" : "#111",
              }}
            >
              {seasonRank ? `#${seasonRank}` : "—"}
            </BodyText>
          </div>

          <div style={{ flex: 1, minWidth: "100px", background: "#f4f7f8", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "0.65rem 0.8rem" }}>
            <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>Season Pos. Rank</BodyText>
            <BodyText
              style={{
                margin: "0.15rem 0 0",
                fontSize: "1.05rem",
                fontWeight: "700",
                color: seasonPositionRank && seasonPositionRank <= 1 ? "#16a34a" : "#111",
              }}
            >
              {seasonPositionRank ? `#${seasonPositionRank}` : "—"}
            </BodyText>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "0.75rem 0.8rem",
            background: "#fff",
          }}
        >
          <BodyText style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}>
            Form by GW
          </BodyText>

          {chartRows.length ? (
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "flex-end",
                gap: "0.45rem",
                height: "140px",
                marginTop: "0.75rem",
                overflowX: "auto",
                paddingBottom: "0.2rem",
              }}
            >
              {(() => {
                const maxValue = Math.max(...chartRows.map((row) => row.value), 1);
                const avgHeight = maxValue ? (avgPointsPerAppPlayer / maxValue) * 92 : 0;

                return (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: `${Math.max(avgHeight, 6)}px`,
                        borderTop: "2px dotted #999",
                        opacity: 0.8,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        bottom: `${Math.max(avgHeight, 6) + 4}px`,
                        fontSize: "0.68rem",
                        color: "#666",
                        fontStyle: "italic",
                        background: "rgba(255,255,255,0.9)",
                        padding: "0 0.2rem",
                        lineHeight: 1,
                      }}
                    >
                      {`${avgPointsPerAppPlayer.toFixed(1)} Avg.`}
                    </div>

                    {chartRows.map((row) => {
                      const barHeight = Math.max((row.value / maxValue) * 92, row.value > 0 ? 12 : 6);
                      return (
                        <div
                          key={row.gw}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            minWidth: "34px",
                            flex: "0 0 auto",
                            height: "100%",
                          }}
                        >
                          <BodyText
                            style={{
                              margin: "0 0 0.25rem",
                              fontSize: "0.72rem",
                              fontWeight: "700",
                              color: "#234",
                              lineHeight: 1,
                            }}
                          >
                            {row.value}
                          </BodyText>
                          <div
                            style={{
                              width: "24px",
                              height: `${barHeight}px`,
                              borderRadius: "8px 8px 4px 4px",
                              background: "#50B4C8",
                            }}
                          />
                          <BodyText
                            style={{
                              margin: "0.35rem 0 0",
                              fontSize: "0.7rem",
                              color: "#666",
                              lineHeight: 1,
                            }}
                          >
                            {row.gw}
                          </BodyText>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          ) : (
            <BodyText style={{ margin: "0.6rem 0 0", fontSize: "0.8rem", color: "#666" }}>
              No form data yet
            </BodyText>
          )}
        </div>

        {/* Bottom prev/next navigation */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1.5rem", paddingTop: "0.6rem", borderTop: "1px solid #f0f0f0", marginTop: "0.75rem" }}>
          <button type="button" onClick={onPrev} disabled={!canPrev} style={{ background: "none", border: "none", fontSize: "0.9rem", fontWeight: "700", color: canPrev ? "#50B4C8" : "#ccc", cursor: canPrev ? "pointer" : "default", padding: "0.3rem 0.5rem", fontFamily: "inherit" }}>‹ Prev</button>
          <button type="button" onClick={onNext} disabled={!canNext} style={{ background: "none", border: "none", fontSize: "0.9rem", fontWeight: "700", color: canNext ? "#50B4C8" : "#ccc", cursor: canNext ? "pointer" : "default", padding: "0.3rem 0.5rem", fontFamily: "inherit" }}>Next ›</button>
        </div>
      </div>
    </div>
  );
}